// Time Conditions — real-time open/closed routing (Asterisk GotoIfTime).
// Build once, then select as a routing target on Inbound DIDs and IVRs.
import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Loader2, Clock, CalendarOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import RelationalPicker from "@/admin/components/RelationalPicker";

const TZ = ["Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Europe/London", "America/New_York", "America/Los_Angeles", "Australia/Sydney"];
const DAYS = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
const DEST_KINDS = [
  { v: "queue", label: "Inbound Queue", refKind: "queues" },
  { v: "ivr", label: "IVR Flow", refKind: "ivr_flows" },
  { v: "voicemail", label: "Voicemail Box", refKind: "voicemail_boxes" },
  { v: "external", label: "External number", refKind: null },
  { v: "hangup", label: "Hang up", refKind: null },
];

function Destination({ label, value, onChange, testId }) {
  const v = value || { kind: "queue", id: "", external_number: "" };
  const meta = DEST_KINDS.find((k) => k.v === v.kind) || DEST_KINDS[0];
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={v.kind} onValueChange={(k) => onChange({ kind: k, id: "", external_number: "" })}>
        <SelectTrigger data-testid={`${testId}-kind`} className="h-9"><SelectValue /></SelectTrigger>
        <SelectContent>{DEST_KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}</SelectContent>
      </Select>
      {meta.refKind ? (
        <RelationalPicker kind={meta.refKind} value={v.id || ""} onChange={(id) => onChange({ ...v, id })} testId={`${testId}-target`} placeholder={`Choose ${meta.label.toLowerCase()}…`} />
      ) : meta.v === "external" ? (
        <Input data-testid={`${testId}-external`} value={v.external_number || ""} onChange={(e) => onChange({ ...v, external_number: e.target.value })} placeholder="+44 20 7946 0958" />
      ) : null}
    </div>
  );
}

export default function TimeConditionsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [holidayCount, setHolidayCount] = useState(0);
  const { canWrite, canDelete } = useCurrentModulePerms();

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/resources/time_conditions");
      setRows(r.data || []);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => {
    load();
    api.get("/tenants/me").then((r) => setHolidayCount((r.data?.holidays || []).length)).catch(() => {});
  }, []);

  function blank() {
    return {
      name: "", is_active: true, timezone: "Asia/Kolkata", use_holidays: true,
      rules: [{ days: ["mon", "tue", "wed", "thu", "fri"], start: "09:00", end: "18:00" }],
      match: { kind: "queue", id: "", external_number: "" },
      no_match: { kind: "voicemail", id: "", external_number: "" },
    };
  }
  function startNew() { setForm(blank()); setEditing("new"); }
  function startEdit(r) {
    const d = r.data || {};
    setForm({
      name: r.name, is_active: !!r.is_active,
      timezone: d.timezone || "Asia/Kolkata",
      use_holidays: d.use_holidays !== false,
      rules: Array.isArray(d.rules) && d.rules.length ? d.rules : blank().rules,
      match: d.match || blank().match,
      no_match: d.no_match || blank().no_match,
    });
    setEditing(r);
  }

  function addRule() { setForm((f) => ({ ...f, rules: [...f.rules, { days: ["mon", "tue", "wed", "thu", "fri"], start: "09:00", end: "18:00" }] })); }
  function updateRule(i, patch) { setForm((f) => ({ ...f, rules: f.rules.map((r, j) => j === i ? { ...r, ...patch } : r) })); }
  function removeRule(i) { setForm((f) => ({ ...f, rules: f.rules.filter((_, j) => j !== i) })); }
  function toggleDay(i, day) {
    setForm((f) => ({
      ...f, rules: f.rules.map((r, j) => {
        if (j !== i) return r;
        const has = (r.days || []).includes(day);
        return { ...r, days: has ? r.days.filter((d) => d !== day) : [...(r.days || []), day] };
      }),
    }));
  }

  async function save() {
    if (!form.name?.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), is_active: !!form.is_active,
        data: {
          timezone: form.timezone, use_holidays: !!form.use_holidays,
          rules: form.rules, match: form.match, no_match: form.no_match,
        },
      };
      if (editing === "new") await api.post("/admin/resources/time_conditions", payload);
      else await api.patch(`/admin/resources/time_conditions/${editing.id}`, payload);
      toast.success("Time Condition saved"); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }
  async function remove(r) {
    if (!confirm(`Delete Time Condition "${r.name}"?`)) return;
    try { await api.delete(`/admin/resources/time_conditions/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  }

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="tc-title">Time Conditions</h1>
          <p className="text-sm text-muted-foreground mt-1">Route inbound calls by business hours & holidays. Pick a Time Condition as the target on any DID or IVR option.</p>
        </div>
        {canWrite && <Button data-testid="add-tc-btn" onClick={startNew} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"><Plus className="w-4 h-4" /> New Time Condition</Button>}
      </div>

      <Card className="overflow-hidden">
        {loading ? <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground"><tr className="text-left">
              <th className="px-4 py-2">Name</th><th className="px-4 py-2">Timezone</th><th className="px-4 py-2">Rules</th><th className="px-4 py-2">Holidays</th><th className="px-4 py-2">Active</th><th className="px-4 py-2 w-24">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-muted/30" data-testid={`row-tc-${r.id}`}>
                  <td className="px-4 py-2.5"><div className="flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /><span className="font-medium">{r.name}</span></div></td>
                  <td className="px-4 py-2.5 text-xs">{r.data?.timezone || "—"}</td>
                  <td className="px-4 py-2.5"><Badge variant="outline">{(r.data?.rules || []).length}</Badge></td>
                  <td className="px-4 py-2.5">{r.data?.use_holidays ? <Badge variant="outline" className="text-[10px]">On</Badge> : <span className="text-xs text-muted-foreground">—</span>}</td>
                  <td className="px-4 py-2.5">{r.is_active ? <Badge variant="outline" className="text-[10px]">On</Badge> : <Badge variant="destructive" className="text-[10px]">Off</Badge>}</td>
                  <td className="px-4 py-2.5"><div className="flex gap-1">
                    {canWrite && <Button data-testid={`edit-tc-${r.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => startEdit(r)}><Edit2 className="w-4 h-4" /></Button>}
                    {canDelete && <Button data-testid={`del-tc-${r.id}`} variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(r)}><Trash2 className="w-4 h-4" /></Button>}
                  </div></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-sm text-muted-foreground">No Time Conditions yet.</td></tr>}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "New Time Condition" : `Edit ${editing?.name}`}</DialogTitle>
            <DialogDescription>If the call lands inside any rule below it routes to the <b>open</b> destination, otherwise the <b>closed</b> destination. Holidays override.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4 py-2">
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2"><Label>Name</Label><Input data-testid="tc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Business Hours — India" /></div>
                <div className="flex items-center gap-2 mt-7"><Switch data-testid="tc-active" checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: !!v })} /><Label>Active</Label></div>
                <div className="sm:col-span-2"><Label>Timezone</Label>
                  <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
                    <SelectTrigger data-testid="tc-tz" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{TZ.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {/* Business hours rules */}
              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-display font-bold">Open hours</h3>
                  <Button data-testid="tc-add-rule" onClick={addRule} variant="outline" size="sm" className="gap-1"><Plus className="w-3.5 h-3.5" /> Add rule</Button>
                </div>
                <div className="space-y-3">
                  {form.rules.map((r, i) => (
                    <Card key={i} className="p-3" data-testid={`tc-rule-${i}`}>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {DAYS.map(([v, lbl]) => {
                          const on = (r.days || []).includes(v);
                          return (
                            <button key={v} type="button" data-testid={`tc-rule-${i}-day-${v}`} onClick={() => toggleDay(i, v)}
                              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-primary/50"}`}>
                              {lbl}
                            </button>
                          );
                        })}
                      </div>
                      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                        <div><Label className="text-xs">Start</Label><Input data-testid={`tc-rule-${i}-start`} type="time" value={r.start || ""} onChange={(e) => updateRule(i, { start: e.target.value })} /></div>
                        <div><Label className="text-xs">End</Label><Input data-testid={`tc-rule-${i}-end`} type="time" value={r.end || ""} onChange={(e) => updateRule(i, { end: e.target.value })} /></div>
                        <Button data-testid={`tc-rule-${i}-rm`} onClick={() => removeRule(i)} variant="ghost" size="icon" className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </Card>
                  ))}
                  {form.rules.length === 0 && <div className="text-xs text-muted-foreground">No rules — every call will use the closed destination.</div>}
                </div>
              </div>

              {/* Holidays */}
              <div className="border-t pt-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <CalendarOff className="w-4 h-4 text-primary" />
                  <div>
                    <Label>Apply tenant holidays</Label>
                    <p className="text-xs text-muted-foreground">Uses the global list on <b>Tenant Hours / Holidays</b> ({holidayCount} configured). Holiday dates route to the closed destination.</p>
                  </div>
                </div>
                <Switch data-testid="tc-use-holidays" checked={!!form.use_holidays} onCheckedChange={(v) => setForm({ ...form, use_holidays: !!v })} />
              </div>

              {/* Destinations */}
              <div className="border-t pt-3 grid sm:grid-cols-2 gap-4">
                <Destination label="Open destination (within hours)" value={form.match} onChange={(d) => setForm({ ...form, match: d })} testId="tc-match" />
                <Destination label="Closed destination (outside hours / holidays)" value={form.no_match} onChange={(d) => setForm({ ...form, no_match: d })} testId="tc-nomatch" />
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button data-testid="tc-save" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
